import { PayloadAction } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import {
	call,
	cancel,
	cancelled,
	debounce,
	delay,
	fork,
	put,
	retry,
	select,
	take,
	takeLatest,
} from 'redux-saga/effects';
import { DeviceStatus, FLAGS, StatusResponsePayload, StoreState } from '@Types';
import * as sensorDetailsActions from '@Actions/sensorDetails';
import {
	getStatus,
	peekConnectionService,
	sensorTroubleshootService,
} from '@Services/device';
import {
	error as snackbarError,
	success,
	warning as snackbarWarn,
} from '@innovyze/stylovyze';
import { getStore, getValue } from '@Selectors';
import {
	DataPeekResponse,
	SensorTroubleShootResponse,
	TroubleshootingRequests,
	SensorTroubleshootingShadow,
} from '@Types/peek';
import { ConnectionPeeker } from '@Pages/EditSensor/ConnectionPeeker';
import { SensorConfigFormShape } from '@Types/configurations.types';
import { Task } from 'redux-saga';

function* getStatusSaga(action: PayloadAction<string>) {
	const { getDeviceStatusResolved, getDeviceStatusRejected } =
		sensorDetailsActions;
	try {
		const response: AxiosResponse<StatusResponsePayload> = yield retry(
			5,
			1500,
			getStatus,
		);
		const deviceId = action.payload;
		if (response.data) {
			const {
				status: {
					payload: { state },
				},
			} = response.data;
			const status = state.reported?.status;
			const command = state.delta?.['sync-command'].command;
			const sensorId = state.delta?.['sync-command'].sensorId;
			let payload: DeviceStatus = DeviceStatus.Unknown;
			// ? COMPLETE status
			if (status === 'complete') {
				payload = DeviceStatus.Complete;
			}
			// ? Device's state is currently resampling (command)
			if (sensorId === deviceId) {
				if (command === 'resample') {
					payload = DeviceStatus.Resample;
				}
			}
			const {
				sensorDetails: { deviceStatus },
			} = yield select(getValue);
			// ? State changed from resample to complete (and sensor is doing nothing)
			if (
				deviceStatus === DeviceStatus.Resample &&
				status === 'complete' &&
				!command
			) {
				yield put(success('Success'));
			}
			yield put(getDeviceStatusResolved(payload));
		} else {
			throw new Error('OperationStatus != SUCCESS');
		}
	} catch (e) {
		console.error(e);
		yield put(getDeviceStatusRejected());
		// yield put(error('Failed'));
	}
}

/**
 * @deprecated
 */
function* peekConnectionSaga(
	action: PayloadAction<sensorDetailsActions.SensorTbltParams>,
) {
	const {
		troubleshootSensor,
		peekSensorConnectionResolved,
		peekSensorConnectionRejected,
	} = sensorDetailsActions;
	try {
		const result: DataPeekResponse | 'timeout' = yield call(
			peekConnectionService,
			action.payload.peekForm,
		);
		// ? empty data results (check tag)
		if (result === 'timeout' || !result?.data?.length) {
			yield put(troubleshootSensor(action.payload));
		} else {
			yield put(peekSensorConnectionResolved(result.data));
		}
	} catch (e) {
		console.error(e);
		yield put(peekSensorConnectionRejected());
		console.error('Peek Failed');
		yield put(troubleshootSensor(action.payload));
	}
}

function* peekFieldChangeSaga(action: PayloadAction<SensorConfigFormShape>) {
	const {
		systemConnections: { list: sysConList },
	}: StoreState = yield select(getValue);
	let query = 'computing...';
	const sysConnection = sysConList.find(
		el =>
			el.SystemConnectionId === action.payload.dataSourceId ||
			el.connectionId === action.payload.dataSourceId,
	);
	if (!sysConList.length) {
		query = 'There is no data source';
	} else if (!sysConnection) {
		query = 'Invalid data source...';
	} else {
		query = ConnectionPeeker.create({
			formData: action.payload,
			sysConnection,
		});
	}
	yield put(sensorDetailsActions.peekSensorComputeQuery(query));
}


function* isTbltEnabled(): Generator<unknown, boolean, unknown> {
	type StoreType = StoreState & {
		features: {
			variations?: { [key: string]: boolean };
		} & {
			[key: string]: boolean | Record<string, unknown>;
		};
	};
	const storeState = (yield select(getStore)) as StoreType;
	const features = storeState?.features || {};
	const variations = features.variations || {};
	const flag = FLAGS.ENABLE_SENSOR_PEEK_TROUBLESHOOT;
	return Boolean(features[flag] || variations[flag]);
}

function* sensorTroubleshootSaga(
	action: PayloadAction<sensorDetailsActions.SensorTbltParams>,
) {
	const {
		troubleshootSensorResolved,
		troubleshootSensorRejected,
		pollPeekAsyncResultsStop,
		pollSensorTroubleshootingStop,
	} = sensorDetailsActions;
	const { peekForm, timeoutMs, skipTblt } = action.payload;
	if (skipTblt) {
		console.warn('sensor troubleshooting is skipped');
		yield put(pollSensorTroubleshootingStop());
		yield put(pollPeekAsyncResultsStop()); // ? restarts peek state to idle
		return;
	}
	const shouldTblt: boolean = yield call(isTbltEnabled);
	if (!shouldTblt) {
		console.warn('sensor troubleshooting is not enabled');
		yield put(pollSensorTroubleshootingStop());
		yield put(pollPeekAsyncResultsStop()); // ? restarts peek state to idle
		return;
	}
	try {
		if (!peekForm.sensorId && !peekForm.tagValue) {
			throw new Error('Invalid sensor id and/or tagValue');
		}
		const response: SensorTroubleShootResponse = yield retry(
			1,
			1500,
			sensorTroubleshootService,
			peekForm,
		);
		if (response.uid && response.operationResult === 'success') {
			yield put(
				troubleshootSensorResolved({
					uuid: response.uid,
					sensorId: peekForm.sensorId,
					timeoutMs,
				}),
			);
		}
	} catch (e) {
		console.error(e);
		yield put(snackbarError('Could not issue sensor troubleshoot'));
		yield put(troubleshootSensorRejected());
	}
}

type PollResponse = AxiosResponse<SensorTroubleshootingShadow>;
function* pollSensorStateSaga(
	action: PayloadAction<{ uuid: string; sensorId: string }>,
) {
	const {
		pollSensorTroubleshootingResolved: pollRes,
		pollSensorTroubleshootingRejected: pollRej,
		pollSensorStateUpdate: pollUpdate,
		pollSensorTroubleshootingStop: pollStop,
	} = sensorDetailsActions;
	const { uuid, sensorId } = action.payload;

	// TODO: make these some helpers
	function getReported(
		sh: SensorTroubleshootingShadow,
	): TroubleshootingRequests {
		return sh?.status?.payload?.state?.reported;
	}

	try {
		if (!uuid) throw new Error('Invalid uuid id');
		const shadowName = `SensorValidate-${uuid}`;
		while (true) {
			const res: PollResponse = yield call(getStatus, shadowName);
			const result = getReported(res.data);
			if (!result || !Object.keys(result).length) yield put(pollStop()); // * Stop polling
			const { state } = result;
			if (state === 'FINISHED') {
				yield put(pollRes({ sensorId, result })); // * Stop success
			} else if (!result) {
				yield put(pollRej()); // * Stop polling (error)
			} else {
				// * Poll continues...
				yield put(pollUpdate({ sensorId, result }));
				yield delay(1000 * 5);
			}
		}
	} catch (e) {
		console.error(e);
		yield put(snackbarError('Poll sensor state failed'));
		yield put(pollRej());
	} finally {
		// * This block gets called if the task (saga) gets cancelled
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		if (yield cancelled()) console.log('poll was cancelled');
	}
}

function* stopPollSensorStateSaga(
	action: PayloadAction<sensorDetailsActions.SensorTbltParams>,
) {
	const { pollSensorTroubleshootingRejected } = sensorDetailsActions;
	const tOut = action.payload.timeoutMs / 1000;
	console.log(`troubleshooting will stop after ${tOut} seconds!`);
	try {
		yield delay(tOut * 1000);
		yield put(snackbarWarn('Troubleshooting timed out'));
		yield put(pollSensorTroubleshootingRejected());
	} catch (error) {
		console.log(error);
	} finally {
		// * This block gets called if the task (saga) gets cancelled
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		if (yield cancelled()) console.log('stop condition was cancelled');
	}
}

function* watchGetStatus() {
	yield takeLatest(sensorDetailsActions.getDeviceStatus, getStatusSaga);
}

/**
 * @deprecated
 */
function* watchPeekConnection() {
	yield takeLatest(
		sensorDetailsActions.peekSensorConnection,
		peekConnectionSaga,
	);
}

function* watchPeekFieldChange() {
	yield debounce(
		256,
		sensorDetailsActions.peekSensorFieldChange,
		peekFieldChangeSaga,
	);
}

function* watchSensorTroubleshoot() {
	yield takeLatest(
		sensorDetailsActions.troubleshootSensor,
		sensorTroubleshootSaga,
	);
}

function* watchPollTestState() {
	const {
		troubleshootSensorResolved,
		pollSensorTroubleshootingStop,
		pollSensorTroubleshootingResolved,
		pollSensorTroubleshootingRejected,
	} = sensorDetailsActions;
	// * Custom implementation, similar to takeLatest but it's cancellable. Check pollSensorStateSaga
	while (true) {
		const action: PayloadAction<sensorDetailsActions.ResolvedTbltParams> =
			yield take([troubleshootSensorResolved]);
		console.log('troubleshootSensorResolved action taken!');
		yield delay(1000 * 3); // ? possible lambda's cold start time
		const task: Task = yield fork(pollSensorStateSaga, action as never);
		const stopConditionTask: Task = yield fork(
			stopPollSensorStateSaga,
			action as never,
		);
		yield take([
			pollSensorTroubleshootingStop,
			pollSensorTroubleshootingResolved,
			pollSensorTroubleshootingRejected,
		]);
		if (task.isRunning()) yield cancel(task);
		if (stopConditionTask.isRunning()) yield cancel(stopConditionTask);
	}
}

export default [
	watchGetStatus(),
	watchPeekConnection(),
	watchPeekFieldChange(),
	watchSensorTroubleshoot(),
	watchPollTestState(),
];
